/**
 * The grid's column filters: in the URL, without losing the one being built.
 *
 * The URL carries only FINISHED conditions (see `writeFilterModel`), and that is
 * right - a column and an operator with no value is not a question, and a link
 * should not say one was asked. But the grid reports exactly that the moment a
 * column is chosen, and when the URL was the grid's ONLY model the refusal threw
 * the choice away: "Filter" on any column's menu, or picking another column in
 * the panel, left the grid holding nothing, and the panel fell back to its
 * placeholder - the first column. Every filter ended up on ID (issue #62).
 *
 * So there are two models. The grid is handed the one it last reported, whole -
 * unfinished conditions and its own item ids included - and the URL is written
 * from that one's finished part. The URL still wins whenever it changes to
 * something this hook did not write: a pasted link, a reload, the sidebar link
 * back to the bare path.
 */

import { useMemo, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import type { GridFilterModel } from '@mui/x-data-grid'
import { FILTER_KEY, readFilterModel, writeFilterModel } from './rowFilter'

/**
 * A model as the URL would carry it, or `null` for no filter at all.
 *
 * Two models with the same answer here ask the same question, whatever else
 * they hold - which is how an unfinished condition is told apart from a change.
 */
export function question(model: GridFilterModel): string | null {
  return writeFilterModel(new URLSearchParams(), model).get(FILTER_KEY)
}

export interface HeldFilters {
  /** What the grid is showing. */
  model: GridFilterModel
  /** The URL's model and the path, as last reconciled against. */
  url: GridFilterModel
  path: string
  /**
   * Questions written to the URL that it has not been seen to settle on.
   *
   * The router applies a write as a transition, so a second write can go out
   * before the first has landed. Without this the first one landing looks like
   * somebody else's navigation, and the grid is reset to it mid-keystroke.
   */
  pending: (string | null)[]
}

/**
 * What to hold once the URL or the path has changed.
 *
 * Pure, and exported so it is tested as a table rather than through a grid.
 */
export function reconcile(held: HeldFilters, url: GridFilterModel, path: string): HeldFilters {
  const adopted: HeldFilters = { model: url, url, path, pending: [] }

  // '/ports' and '/states' are one mounted component. A condition half-built on
  // one kind's column is nothing the next kind should open its panel on.
  if (path !== held.path) return adopted

  const asked = question(url)
  if (asked === question(held.model)) return { ...held, url, pending: [] }
  if (held.pending.includes(asked)) return { ...held, url }
  return adopted
}

export function useColumnFilters() {
  const [params, setParams] = useSearchParams()
  const { pathname } = useLocation()

  /** What the URL says: the finished conditions, and what a link reproduces. */
  const applied = useMemo(() => readFilterModel(params), [params])

  const [held, setHeld] = useState<HeldFilters>(() => ({
    model: applied,
    url: applied,
    path: pathname,
    pending: [],
  }))

  // Adjusted during render rather than in an effect, so the grid is never
  // handed one render of a model the URL has already replaced.
  let current = held
  if (held.url !== applied || held.path !== pathname) {
    current = reconcile(held, applied, pathname)
    setHeld(current)
  }

  const setFilterModel = (next: GridFilterModel) => {
    const asked = question(next)
    // Against the last question WRITTEN, not the one the URL shows: a write
    // still in flight is where the URL is going.
    const heading = current.pending.length > 0 ? current.pending.at(-1) : question(applied)
    const changes = asked !== heading

    setHeld((was) => ({
      ...was,
      model: next,
      pending: changes ? [...was.pending, asked] : was.pending,
    }))
    // Replace, not push: building a filter must not fill up the back button.
    if (changes) setParams((now) => writeFilterModel(now, next), { replace: true })
  }

  return { filterModel: current.model, applied, setFilterModel }
}
